<template>
  <div class="cash-call-autocomplete position-relative">
    <BaseInput
        :value="value"
        :label="label"
        :name="name"
        :size="size"
        :required="required"
        :rules="rules"
        :placeholder="placeholder"
        :disabled="disabled"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
    >
      <template v-if="$slots.label" v-slot:label>
        <slot name="label" />
      </template>
    </BaseInput>

    <ul v-if="showSuggestions && !disabled && filteredOptions.length" class="cash-call-autocomplete__list">
      <li
          v-for="option in filteredOptions"
          :key="option"
          @mousedown.prevent="selectOption(option)"
      >
        {{ option }}
      </li>
    </ul>
  </div>
</template>

<script>
import { defineComponent, ref, computed } from '@vue/composition-api'
import { BaseInput } from '@components/base'

export default defineComponent({
  name: 'CashCallAutocompleteInput',
  components: {
    BaseInput
  },
  props: {
    value: {
      type: [String, Number],
      default: ''
    },
    label: String,
    // Validation field name, used by vee-validate to interpolate the {field}
    // placeholder in messages ("The Broker field is required"). Needed when the
    // label is provided via the <template #label> slot rather than the label
    // prop - without it the message shows the literal "{field}".
    name: String,
    // Candidate values for this field, already cross-filtered against the
    // other fields' current selections by the caller (facetedFilter) - this
    // component only narrows that pool further by what's typed so far.
    options: {
      type: Array,
      default: () => []
    },
    size: {
      type: String,
      default: 'sm'
    },
    required: Boolean,
    rules: String,
    placeholder: String,
    disabled: Boolean,
    // Max suggestions to render. The list is scrollable (max-height + overflow),
    // so fields backed by a full system list (Lead Underwriter / Broker - "a
    // drop-down of ALL companies in the system") pass a large cap to show the
    // whole sorted list on focus, while cache-faceted fields keep the small
    // default so a huge cache doesn't render hundreds of rows at once.
    maxOptions: {
      type: Number,
      default: 8
    },
    // Sort the suggestion list alphabetically. Company lists read better sorted;
    // cache-faceted fields keep source order (default off).
    sortOptions: {
      type: Boolean,
      default: false
    }
  },
  emits: ['input', 'focus', 'blur', 'select'],
  setup(props, { emit }) {
    const showSuggestions = ref(false)

    const filteredOptions = computed(() => {
      let pool = [...new Set(props.options.filter((opt) => opt !== null && opt !== undefined && opt !== ''))]
      if (props.sortOptions) {
        pool = pool.sort((a, b) => String(a).localeCompare(String(b)))
      }
      const query = String(props.value || '').trim().toLowerCase()
      const matches = query ? pool.filter((opt) => String(opt).toLowerCase().includes(query)) : pool
      return matches.slice(0, props.maxOptions)
    })

    const onInput = (newValue) => {
      emit('input', newValue)
      showSuggestions.value = true
    }

    // Bubbled up so a parent with a large candidate pool (e.g. an
    // autocomplete backed by a big cache) can compute options for only
    // whichever field currently has focus, instead of every field at once.
    const onFocus = () => {
      showSuggestions.value = true
      emit('focus')
    }

    const onBlur = () => {
      showSuggestions.value = false
      emit('blur')
    }

    const selectOption = (option) => {
      emit('input', option)
      // Distinct from 'input': fires ONLY when the user explicitly picks a
      // suggestion (not while typing). Lets a parent auto-populate related
      // fields only on a real selection, so a partial value that happens to
      // match (e.g. typing "2") doesn't trigger prefill.
      emit('select', option)
      showSuggestions.value = false
    }

    return {
      showSuggestions,
      filteredOptions,
      onInput,
      onFocus,
      onBlur,
      selectOption
    }
  }
})
</script>

<style scoped>
.cash-call-autocomplete__list {
  position: absolute;
  /* Above the modal body and any table/stacking context: the inline-edit rows
     render this inside a <td>, so a low z-index left it painted behind the card. */
  z-index: 1080;
  top: 100%;
  left: 0;
  /* Auto-size to the widest option (min = the input width) instead of being
     pinned to a narrow cell, so long broker/underwriter names aren't clipped.
     Capped so it never runs off-screen; only vertical scrolling. */
  right: auto;
  min-width: 100%;
  width: max-content;
  max-width: 320px;
  margin: 2px 0 0;
  padding: 4px 0;
  list-style: none;
  background-color: #fff;
  border: 1px solid #e3e8ec;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  /* ~4 visible options (each li is 6px+6px padding + ~19px line = ~31px).
     Kept short deliberately: at 200px the open list covered the row beneath
     it in the inline-edit table. Every autocomplete in the app shares this
     rule, so all dropdowns open to the same height. */
  max-height: 132px;
  overflow-x: hidden;
  overflow-y: auto;
}

.cash-call-autocomplete__list li {
  padding: 6px 12px;
  font-size: 0.875rem;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cash-call-autocomplete__list li:hover {
  background-color: #f1f3f5;
  color: #1c4a50;
}
</style>
